import { useEffect, useMemo, useState } from "react";
import { Drawer } from "antd";
import dayjs from "dayjs";
import {
    AlertTriangle, ArrowDownLeft, ArrowUpRight, ChevronLeft, ChevronRight, CornerUpLeft,
    CornerUpRight, Eye, File, FileImage, FileSpreadsheet, FileText, Loader2, MessageSquareReply,
    MousePointerClick, Send, X,
} from "lucide-react";
import useApiMethods from "../../../lib/useApiMethods";
import { EMAIL_MESSAGES_URL } from "../../../constants/api";
import { cn } from "../../../utils/tailwindUtils";
import { avatarTone, cleanSubject, fromNow, initials, makePersonResolver } from "./emailThreads";

/**
 * Reading panel for the lead page's Emails tab: the whole conversation.
 *
 * Each message comes from email/messages/<id>/ (trustedge
 * platform_core/email_integration/message_detail.py) with tracking removed, so
 * reading a sent email here never counts as the customer opening it — only
 * `view_html` is ever rendered, never the stored (tracked) body.
 *
 * Bodies render in a sandboxed iframe with neither allow-scripts nor
 * allow-same-origin: a received email is someone else's HTML and must not run
 * script or reach the CRM's session. Because the frame is opaque to us its
 * content height cannot be measured, so it is sized from the text length.
 */

const fmt = (d) => (d ? dayjs(d).format("DD MMM YYYY, HH:mm") : "");

const FRAME_HEAD = '<base target="_blank">'
    + '<meta http-equiv="Content-Security-Policy" content="script-src \'none\'; object-src \'none\'">'
    + '<style>html,body{margin:0}body{padding:4px 2px 12px;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;'
    + 'font-size:14px;line-height:1.55;color:#1f2937;word-wrap:break-word}img{max-width:100%;height:auto}'
    + 'a{color:#7c3aed}blockquote{margin:0 0 0 6px;padding-left:10px;border-left:3px solid #e5e7eb;color:#6b7280}'
    + 'table{max-width:100%}</style>';

/** Plain text of an email, for quoting into a reply. */
export function emailPlainText(em) {
    if (em?.body_text?.trim()) return em.body_text;
    const html = em?.view_html || em?.body_html || "";
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("style,script,head,title").forEach(n => n.remove());
    return (doc.body?.innerText || doc.body?.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
}

const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Compose prefill for Reply / Forward. */
export function replyDraft(em, mode) {
    const quoted = escapeHtml(emailPlainText(em)).replace(/\n/g, "<br>");
    const subj = (em.subject || "").trim();
    if (mode === "forward") {
        return {
            to: "", cc: "",
            subject: /^fwd?:/i.test(subj) ? subj : `Fwd: ${subj}`,
            body_html: `<p></p><p>---------- Forwarded message ----------<br>From: ${escapeHtml(em.from_email || "")}<br>`
                + `Date: ${fmt(em.sent_at)}<br>Subject: ${escapeHtml(subj)}<br>To: ${escapeHtml(em.to_email || "")}</p>`
                + `<p>${quoted}</p>`,
        };
    }
    return {
        // Replying to our own sent email goes to the customer again, not to us.
        to: em.direction === "sent" ? em.to_email : em.from_email,
        cc: "",
        subject: /^re:/i.test(subj) ? subj : `Re: ${subj}`,
        body_html: `<p></p><p>On ${fmt(em.sent_at)}, ${escapeHtml(em.from_email || "")} wrote:</p>`
            + `<blockquote style="margin:0 0 0 8px;padding-left:8px;border-left:2px solid #d1d5db;color:#6b7280">${quoted}</blockquote>`,
    };
}

/** Frame height from content size — see the note at the top of the file. */
function frameHeight(em) {
    const text = emailPlainText(em);
    const lines = text.split("\n").reduce((n, l) => n + Math.max(1, Math.ceil(l.length / 85)), 0);
    const images = Math.min(4, ((em.view_html || "").match(/<img\b/gi) || []).length);
    return Math.max(140, Math.min(1400, 40 + lines * 22 + images * 180));
}

function fileIcon(name) {
    const ext = (name.split(".").pop() || "").toLowerCase();
    if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) return FileImage;
    if (["xls", "xlsx", "csv"].includes(ext)) return FileSpreadsheet;
    if (["pdf", "doc", "docx", "txt", "ppt", "pptx"].includes(ext)) return FileText;
    return File;
}

/** Sent → Opened → Clicked → Replied, each step dated once it happens. */
function Journey({ em }) {
    if (em.direction !== "sent") return null;
    if (em.is_bounced) {
        return (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span><b className="font-semibold">Bounced</b>{em.bounce_reason && ` — ${em.bounce_reason}`}</span>
            </div>
        );
    }
    const steps = [
        { icon: Send, label: "Sent", done: true, at: em.sent_at },
        { icon: Eye, label: em.is_opened ? `Opened${em.open_count > 1 ? ` ${em.open_count}×` : ""}` : "Not opened yet",
          done: em.is_opened, at: em.opened_at },
        { icon: MousePointerClick, label: em.is_clicked ? `Clicked${em.click_count > 1 ? ` ${em.click_count}×` : ""}` : "No clicks",
          done: em.is_clicked, at: em.clicked_at },
        { icon: MessageSquareReply, label: em.is_replied ? "Replied" : "No reply", done: em.is_replied, at: em.replied_at },
    ];
    return (
        <ol className="flex flex-wrap items-center gap-y-2 rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800/60" aria-label="Email progress">
            {steps.map((s, i) => (
                <li key={s.label} className="flex items-center">
                    {i > 0 && <span className={cn("mx-2 h-px w-5 sm:w-8", s.done ? "bg-emerald-400" : "bg-gray-300 dark:bg-gray-600")} />}
                    <span className={cn("flex h-5 w-5 items-center justify-center rounded-full",
                        s.done ? "bg-emerald-500 text-white" : "bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500")}>
                        <s.icon className="h-3 w-3" />
                    </span>
                    <span className="ml-1.5 text-[11px] leading-tight">
                        <span className={cn("block font-medium", s.done ? "text-gray-800 dark:text-gray-100" : "text-gray-400 dark:text-gray-500")}>{s.label}</span>
                        {s.done && s.at && <span className="block text-gray-400 dark:text-gray-500" title={fmt(s.at)}>{fromNow(s.at)}</span>}
                    </span>
                </li>
            ))}
        </ol>
    );
}

function Message({ em, person, expanded, onToggle, isLast }) {
    const from = person(em.from_email, em);
    const sent = em.direction === "sent";
    const to = (em.to_email || "").split(",").map(a => a.trim()).filter(Boolean).map(a => person(a, em).name);

    if (!expanded) {
        return (
            <button type="button" onClick={onToggle} aria-label={`Expand email from ${from.name}`}
                className="flex w-full items-center gap-3 border-b border-gray-100 px-5 py-3 text-left transition-colors hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50">
                <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold", avatarTone(from))}>
                    {initials(from.name)}
                </div>
                <span className="w-32 shrink-0 truncate text-sm font-medium text-gray-700 dark:text-gray-200">{from.name}</span>
                <span className="min-w-0 flex-1 truncate text-xs text-gray-500 dark:text-gray-400">{em.snippet || emailPlainText(em).slice(0, 160)}</span>
                <span className="shrink-0 text-[11px] text-gray-400" title={fmt(em.sent_at)}>{fromNow(em.sent_at)}</span>
            </button>
        );
    }

    return (
        <article className={cn("px-5 py-4", !isLast && "border-b border-gray-100 dark:border-gray-800")}>
            <header className="flex items-start gap-3">
                <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold", avatarTone(from))}>
                    {initials(from.name)}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{from.name}</span>
                        <span className="truncate text-xs text-gray-500 dark:text-gray-400">&lt;{em.from_email}&gt;</span>
                        {from.lead && from.role && (
                            <span className="rounded bg-gray-100 px-1.5 text-[10px] text-gray-600 dark:bg-gray-700 dark:text-gray-300">{from.role}</span>
                        )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-gray-500 dark:text-gray-400">
                        {sent
                            ? <ArrowUpRight className="h-3.5 w-3.5 text-sky-500" aria-hidden="true" />
                            : <ArrowDownLeft className="h-3.5 w-3.5 text-emerald-500" aria-hidden="true" />}
                        <span>to {to.join(", ") || em.to_email}</span>
                        {em.cc && <span>· cc {em.cc}</span>}
                        {em.bcc && <span>· bcc {em.bcc}</span>}
                        {em.template_name && <span className="rounded bg-primary/10 px-1.5 text-[10px] font-medium text-primary">{em.template_name}</span>}
                    </div>
                </div>
                <button type="button" onClick={onToggle} title={fmt(em.sent_at)} aria-label={`Collapse email from ${from.name}`}
                    className="shrink-0 text-right text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    <span className="block">{dayjs(em.sent_at).format("D MMM, HH:mm")}</span>
                    <span className="block">{fromNow(em.sent_at)}</span>
                </button>
            </header>

            <div className="mt-3 space-y-3 sm:pl-[52px]">
                <Journey em={em} />

                {em.view_html ? (
                    <iframe title={`Email: ${em.subject || ""}`} className="w-full rounded-md bg-white"
                        style={{ height: frameHeight(em) }}
                        sandbox="allow-popups allow-popups-to-escape-sandbox"
                        srcDoc={FRAME_HEAD + em.view_html} />
                ) : em.body_text ? (
                    <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-gray-800 dark:text-gray-100">{em.body_text}</pre>
                ) : (
                    <p className="text-sm italic text-gray-400">This email has no content.</p>
                )}

                {em.attachment_names?.length > 0 && (
                    <div>
                        <p className="mb-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                            {em.attachment_names.length} attachment{em.attachment_names.length === 1 ? "" : "s"}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {em.attachment_names.map(n => {
                                const Icon = fileIcon(n);
                                return (
                                    <span key={n} className="inline-flex max-w-[220px] items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                                        <Icon className="h-4 w-4 shrink-0 text-primary" />
                                        <span className="truncate">{n}</span>
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </article>
    );
}

export default function EmailReader({
    emailId, threadIds = [], ids = [], onNavigate, onClose, onReply, canSend, contacts = [], accounts = [],
}) {
    const { fetch } = useApiMethods();
    const [messages, setMessages] = useState(null);
    const [error, setError] = useState("");
    const [expanded, setExpanded] = useState(() => new Set());
    const person = useMemo(() => makePersonResolver(contacts, accounts), [contacts, accounts]);

    const idsKey = (threadIds.length ? threadIds : [emailId]).join(",");
    useEffect(() => {
        if (!emailId) return;
        let live = true;
        setMessages(null); setError("");
        const wanted = idsKey.split(",").map(Number);
        Promise.all(wanted.map(id => fetch(`${EMAIL_MESSAGES_URL}${id}/`))).then(results => {
            if (!live) return;
            const ok = results.filter(r => r?.status === "success").map(r => r.data);
            if (!ok.length) { setError(results[0]?.message || "Could not load this email"); return; }
            ok.sort((a, b) => new Date(a.sent_at || 0) - new Date(b.sent_at || 0));
            setMessages(ok);
            // The newest message is open; so is the one that was clicked, if different.
            setExpanded(new Set([ok[ok.length - 1].id, emailId]));
        }).catch(() => live && setError("Could not load this email"));
        return () => { live = false; };
    }, [emailId, idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const pos = ids.indexOf(emailId);
    const prevId = pos > 0 ? ids[pos - 1] : null;
    const nextId = pos >= 0 && pos < ids.length - 1 ? ids[pos + 1] : null;

    useEffect(() => {
        if (!emailId) return;
        const onKey = (e) => {
            if (e.target.closest?.("input,textarea,[contenteditable]")) return;
            if (e.key === "ArrowUp" && prevId) onNavigate(prevId);
            if (e.key === "ArrowDown" && nextId) onNavigate(nextId);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [emailId, prevId, nextId, onNavigate]);

    const latest = messages?.[messages.length - 1];
    const toggle = (id) => setExpanded(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const participants = messages
        ? [...new Map(messages.map(m => { const p = person(m.from_email, m); return [p.address, p]; })).values()]
        : [];
    const replyTo = latest && person(latest.direction === "sent" ? (latest.to_email || "").split(",")[0] : latest.from_email, latest);

    const navBtn = "flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 dark:border-gray-700 dark:hover:bg-gray-800";
    const actBtn = "inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700";

    return (
        <Drawer open={!!emailId} onClose={onClose} placement="right" closable={false} destroyOnHidden
            width="min(820px, 100vw)" styles={{ body: { padding: 0, display: "flex", flexDirection: "column" } }}>
            {/* ── top bar ── */}
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2.5 dark:border-gray-700">
                <button type="button" className={navBtn} disabled={!prevId} onClick={() => onNavigate(prevId)}
                    aria-label="Newer email" title="Newer conversation (↑)"><ChevronLeft className="h-4 w-4" /></button>
                <button type="button" className={navBtn} disabled={!nextId} onClick={() => onNavigate(nextId)}
                    aria-label="Older email" title="Older conversation (↓)"><ChevronRight className="h-4 w-4" /></button>
                {pos >= 0 && <span className="text-[11px] text-gray-400">{pos + 1} of {ids.length}</span>}
                <div className="flex-1" />
                {latest && canSend && (
                    <>
                        <button type="button" className={actBtn} onClick={() => onReply(replyDraft(latest, "reply"))}>
                            <CornerUpLeft className="h-3.5 w-3.5" /> Reply
                        </button>
                        <button type="button" className={actBtn} onClick={() => onReply(replyDraft(latest, "forward"))}>
                            <CornerUpRight className="h-3.5 w-3.5" /> Forward
                        </button>
                    </>
                )}
                <button type="button" onClick={onClose} aria-label="Close"
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
                    <X className="h-4 w-4" />
                </button>
            </div>

            {!messages && !error && (
                <div className="flex flex-1 items-center justify-center text-xs text-gray-400">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading conversation…
                </div>
            )}
            {error && <div className="p-6 text-sm text-red-600">{error}</div>}

            {messages && (
                <>
                    <div className="min-h-0 flex-1 overflow-y-auto">
                        {/* ── conversation header ── */}
                        <div className="border-b border-gray-200 px-5 pb-4 pt-5 dark:border-gray-700">
                            <h2 className="break-words text-xl font-semibold leading-snug text-gray-900 dark:text-white">
                                {cleanSubject(messages[0].subject)}
                            </h2>
                            <div className="mt-2.5 flex flex-wrap items-center gap-2">
                                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                                    {messages.length} message{messages.length === 1 ? "" : "s"}
                                </span>
                                <div className="flex -space-x-1.5">
                                    {participants.slice(0, 5).map(p => (
                                        <span key={p.address} title={`${p.name} <${p.address}>`}
                                            className={cn("flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-semibold ring-2 ring-white dark:ring-gray-900", avatarTone(p))}>
                                            {initials(p.name)}
                                        </span>
                                    ))}
                                </div>
                                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                    {participants.map(p => p.name).join(", ")}
                                </span>
                                <span className="ml-auto text-[11px] text-gray-400">
                                    Started {fromNow(messages[0].sent_at)} · last {fromNow(latest.sent_at)}
                                </span>
                            </div>
                        </div>

                        {messages.map((m, i) => (
                            <Message key={m.id} em={m} person={person} isLast={i === messages.length - 1}
                                expanded={expanded.has(m.id)} onToggle={() => toggle(m.id)} />
                        ))}
                    </div>

                    {/* ── reply bar ── */}
                    {canSend && latest && (
                        <div className="flex items-center gap-2 border-t border-gray-200 bg-gray-50/80 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/60">
                            <button type="button" onClick={() => onReply(replyDraft(latest, "reply"))}
                                className="flex h-10 flex-1 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 text-left text-sm text-gray-400 shadow-sm transition-colors hover:border-primary/40 dark:border-gray-700 dark:bg-gray-800">
                                <CornerUpLeft className="h-4 w-4 text-gray-400" />
                                Write a reply to {replyTo?.name || "them"}…
                            </button>
                            <button type="button" onClick={() => onReply(replyDraft(latest, "forward"))} title="Forward"
                                aria-label="Forward conversation"
                                className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                                <CornerUpRight className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                </>
            )}
        </Drawer>
    );
}

